import { Component, type ErrorInfo, type ReactNode } from "react";

import { reportLovableError } from "@/lib/lovable-error-reporting";

type Props = {
  /** Short label used in the fallback copy, e.g. "sign in" or "Today". */
  label?: string | undefined;
  children: ReactNode;
};

type State = { error: Error | null };

/**
 * Keeps a single failing panel from taking down the whole page. Anything that
 * throws inside renders an inline retry card instead of the full-page error screen.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack);
    reportLovableError(error, { boundary: "section_error_boundary" });
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="panel p-6">
        <p className="text-sm font-semibold">
          We couldn't load {this.props.label ?? "this section"}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Something went wrong here, but the rest of the page still works.
        </p>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Try again
        </button>
      </div>
    );
  }
}
